import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Alert,
} from '@cloudscape-design/components';
import { TaskDetails } from '../../components/task/TaskDetails';
import {
  TaskPerformanceForm,
  TaskPerformanceFormData,
} from '../../components/task/TaskPerformanceForm';
import { ChangeAssigneeModal } from '../../components/modals/ChangeAssigneeModal';
import { CancelTaskModal } from '../../components/modals/CancelTaskModal';
import ReopenTaskModal from '../../components/task/ReopenTaskModal';
import { useTasks } from 'src/store/tasks/useTasks';
import useUserProfile from 'src/hooks/useUserProfile';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { ChangeLog } from '../../components/task/ChangeLog';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import { Persona } from 'src/contstant/Persona';
import { createAssigneeChangeComment } from 'src/utils/commentUtils';
import { getTaskContent } from 'src/helper/taskHelper';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { TaskAuditQuestionResponsePanel } from 'src/components/task/TaskAuditQuestionResponsePanel';
import { SplitPanelErrorBoundary } from 'src/components/SplitPanelErrorBoundary';
import { BusinessUnitMismatchHandler } from 'src/components/BusinessUnitMismatchHandler';

const MAX_TASK_REOPENS = 1;

export const TaskDetailsPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlTaskId = searchParams.get('taskId');
  const taskId = location.state?.data?.taskId || urlTaskId;
  const {
    getUserBusinessUnitId,
    getUserId,
    getPersona,
    getUserProgramId,
    getDivisions,
    getUserSelectedLanguage,
  } = useUserProfile();
  const { checkUserPermissions, userPermission, currentUser } = useUsers();
  const { setContent, setTitle, closeView, setPanelSize } = useSplitPanel();

  // Deep-link business unit mismatch detection (mirrors the shared-filters flow).
  // When the URL carries a businessUnitId that differs from the user's profile BU,
  // we must NOT load the task under the wrong BU — instead prompt the user to switch.
  // No mismatch when the param is absent (in-app navigation via location.state) or
  // equals the profile BU, so existing behavior is preserved.
  const urlBusinessUnitId = searchParams.get('businessUnitId');
  const currentBusinessUnitId = getUserBusinessUnitId();
  const isBUMismatch = !!urlBusinessUnitId && urlBusinessUnitId !== currentBusinessUnitId;

  // Gate the modal (and content) on the profile BU being loaded. On a cold deep-link
  // the profile resolves async, so currentBusinessUnitId is '' on first render and
  // isBUMismatch is true even for a legitimate link to the user's OWN BU — which would
  // flash the switch-BU modal. isBUMismatch still gates the LOAD (getTask never fires
  // while the BU is unknown/empty or mismatched); once the profile resolves it either
  // matches (isBUMismatch=false → load proceeds) or truly differs (showBUMismatch=true
  // → modal shows). selectedTask is null while the load is gated, so the content guard
  // below is safe.
  const showBUMismatch = isBUMismatch && !!currentBusinessUnitId;

  // Use task data from navigation state if available, otherwise use taskId from URL
  const taskFromState = location.state?.data;
  const {
    selectedTask,
    getTask,
    getTaskApiStatus,
    updateAssignee,
    updateAssigneeApiStatus,
    auditorUpdate,
    auditorUpdateApiStatus,
    cancelTask,
    cancelTaskApiStatus,
    reopenTask,
    reopenTaskApiStatus,
    clearAPIStatusMessage,
  } = useTasks();

  const [taskStatus, setTaskStatus] = useState('');
  const [showChangeAssigneeModal, setShowChangeAssigneeModal] = useState(false);
  const [showCancelTaskModal, setShowCancelTaskModal] = useState(false);
  const [showReopenTaskModal, setShowReopenTaskModal] = useState(false);

  const [currentFormData, setCurrentFormData] = useState<TaskPerformanceFormData | null>(
    null,
  );

  const canPerformTask = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.TASK.PERFORM_TASK,
      userPermission?.permissions,
    );
  }, [getPersona, userPermission?.permissions]);

  const canCancelTask = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.TASK.CANCEL_TASK,
      userPermission?.permissions,
    );
  }, [getPersona, userPermission?.permissions]);

  const canReopenTask = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.TASK.REOPEN_TASK,
      userPermission?.permissions,
    );
  }, [getPersona, userPermission?.permissions]);

  /**
   * Determines if the task is in read-only mode
   * Read-only when user lacks permissions, assigned to someone else, or task is completed/cancelled
   */
  const isReadOnlyMode = useCallback((): boolean => {
    if (!selectedTask) return false;

    // Task is read-only if user doesn't have perform task permission
    if (!canPerformTask()) {
      return true;
    }

    // Task is read-only if completed or cancelled (but NOT overdue)
    if (
      selectedTask.taskStatus === TaskStatus.COMPLETED ||
      selectedTask.taskStatus === TaskStatus.CANCELLED
    ) {
      return true;
    }

    // Task is read-only if assigned to someone else (case-insensitive — logins are case-insensitive)
    return (
      Boolean(selectedTask.assigneeLogin) &&
      selectedTask.assigneeLogin?.toLowerCase() !== getUserId()?.toLowerCase()
    );
  }, [selectedTask, getUserId, canPerformTask]);

  useEffect(() => {
    // Don't load the task when the URL's BU differs from the profile BU
    if (isBUMismatch) {
      return;
    }
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [
          permissionAccessConfig.TASK.PERFORM_TASK,
          permissionAccessConfig.TASK.CANCEL_TASK,
          permissionAccessConfig.TASK.REOPEN_TASK,
        ],
        divisions: getDivisions(),
      });
    }

    const currentTaskId = taskFromState?.taskId || taskId;
    getTask({
      businessUnitId: getUserBusinessUnitId(),
      taskId: currentTaskId || '',
    });
  }, [
    taskFromState?.taskId,
    taskId,
    getUserBusinessUnitId,
    getTask,
    currentUser,
    getPersona,
    getUserId,
    getUserProgramId,
    getDivisions,
    checkUserPermissions,
    isBUMismatch,
  ]);

  useEffect(() => {
    if (selectedTask) {
      setTaskStatus(selectedTask.taskStatus);
    }
  }, [selectedTask]);

  // Close split panel when navigating away from this page
  useEffect(() => {
    return () => {
      closeView();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refetch task data when operations are successful
  useEffect(() => {
    if (isBUMismatch) {
      return;
    }
    const currentTaskId = taskFromState?.taskId || taskId;
    if (
      (auditorUpdateApiStatus.isSuccess ||
        updateAssigneeApiStatus.isSuccess ||
        cancelTaskApiStatus.isSuccess) &&
      currentTaskId
    ) {
      getTask({
        businessUnitId: getUserBusinessUnitId(),
        taskId: currentTaskId,
      });
    }
  }, [
    auditorUpdateApiStatus.isSuccess,
    updateAssigneeApiStatus.isSuccess,
    cancelTaskApiStatus.isSuccess,
    isBUMismatch,
  ]);

  // Initial task load
  useEffect(() => {
    if (isBUMismatch) {
      return;
    }
    const currentTaskId = taskFromState?.taskId || taskId;
    if (currentTaskId) {
      getTask({
        businessUnitId: getUserBusinessUnitId(),
        taskId: currentTaskId,
      });
    }
  }, [isBUMismatch]);

  /**
   * Check if current user can change task assignee
   * Show button if user has PerformTask permission OR task is assigned to them
   */
  const canChangeAssignee = useCallback((): boolean => {
    if (!selectedTask) return false;

    const persona = getPersona();
    const userId = getUserId();

    // Platform Admin can always change assignee
    if (persona === Persona.PLATFORM_ADMIN) {
      return true;
    }

    // If user has PerformTask permission, they can change assignee
    if (canPerformTask()) {
      return true;
    }

    // If task is assigned to current user, they can change assignee (case-insensitive)
    if (selectedTask.assigneeLogin?.toLowerCase() === userId?.toLowerCase()) {
      return true;
    }

    return false;
  }, [selectedTask, getPersona, getUserId, canPerformTask]);

  const handleChangeAssignee = () => {
    if (canChangeAssignee()) {
      setShowChangeAssigneeModal(true);
    }
  };

  const handleAssigneeChange = async (data: { newAssignee: string; comment: string }) => {
    if (!taskId || !selectedTask) return;

    await updateAssignee({
      businessUnitId: getUserBusinessUnitId(),
      taskId,
      assigneeLogin: data.newAssignee,
      comments: createAssigneeChangeComment(
        data.comment || `Task assigned to ${data.newAssignee}`,
      ),
      lastUpdatedBy: getUserId(),
    });

    await getTask({
      businessUnitId: getUserBusinessUnitId(),
      taskId,
    });

    setShowChangeAssigneeModal(false);
  };

  const handleSave = async (formData: TaskPerformanceFormData) => {
    if (!taskId || !selectedTask) return;

    // Store form data for later use with status changes
    setCurrentFormData(formData);

    // Use the media IDs map from form data (already contains real uploaded media IDs)
    const finalMediaIdsMap = formData.mediaIdsMap || {};

    // Ensure due date is in proper ISO format
    const dueDateToUse = formData.dueDate || selectedTask.dueDate;
    const formattedDueDate =
      dueDateToUse && !dueDateToUse.includes('T')
        ? new Date(dueDateToUse + 'T00:00:00.000Z').toISOString()
        : dueDateToUse;

    const updatePayload = {
      businessUnitId: getUserBusinessUnitId(),
      taskId,
      taskStatus: selectedTask.taskStatus, // Keep current status
      actionPerformed:
        formData.actionPerformed.trim() || selectedTask.actionPerformed || '',
      comments: formData.comments.trim() || selectedTask.comments || '',
      dueDate: formattedDueDate,
      taskMediaIdsMap: finalMediaIdsMap,
      lastUpdatedBy: getUserId(),
      isDraft: true, // Flag to indicate this is a draft save
    };

    // Update the task with the current form data (draft mode - no ChangeLog update)
    await auditorUpdate(updatePayload);
  };

  const handleFormSubmit = async (formData: TaskPerformanceFormData) => {
    // This is kept for backward compatibility but not used with current UI
    await handleSave(formData);
  };

  const handleCancelTask = () => {
    setShowCancelTaskModal(true);
  };

  const handleTaskCancellation = async (data: { reason: string }) => {
    if (!taskId) return;

    await cancelTask({
      businessUnitId: getUserBusinessUnitId(),
      taskId,
      comments: data.reason,
      cancelledBy: getUserId(),
    });

    setShowCancelTaskModal(false);
  };

  const handleStatusChange = async (
    newStatus: 'in-progress' | 'completed',
    comment?: string,
    formData?: TaskPerformanceFormData,
  ) => {
    if (!taskId || !selectedTask) return;

    const dataToUse = formData || currentFormData;
    if (!dataToUse) return;

    const statusMap = {
      'in-progress': TaskStatus.IN_PROGRESS,
      'completed': TaskStatus.COMPLETED,
    };

    // Use the media IDs map from form data (already contains real uploaded media IDs)
    const finalMediaIdsMap = dataToUse.mediaIdsMap || {};

    // Ensure due date is in proper ISO format - prioritize form data over selectedTask
    const dueDateToUse =
      dataToUse.dueDate !== undefined && dataToUse.dueDate !== ''
        ? dataToUse.dueDate
        : selectedTask.dueDate;
    const formattedDueDate =
      dueDateToUse && !dueDateToUse.includes('T')
        ? new Date(dueDateToUse + 'T00:00:00.000Z').toISOString()
        : dueDateToUse;

    await auditorUpdate({
      businessUnitId: getUserBusinessUnitId(),
      taskId,
      taskStatus: statusMap[newStatus],
      actionPerformed: dataToUse.actionPerformed?.trim() || '',
      comments: comment || dataToUse.comments || '',
      dueDate: formattedDueDate,
      taskMediaIdsMap: finalMediaIdsMap,
      lastUpdatedBy: getUserId(),
    });

    // Refetch task data to get updated change log
    await getTask({
      businessUnitId: getUserBusinessUnitId(),
      taskId,
    });
  };

  const taskContent = useMemo(
    () => (selectedTask ? getTaskContent(selectedTask, getUserSelectedLanguage()) : null),
    [selectedTask, getUserSelectedLanguage],
  );

  /**
   * Opens the side panel with question details and audit response
   * Wrapped in error boundary for better error handling
   */
  const handleViewAuditQuestionResponse = useCallback(() => {
    if (!selectedTask || !selectedTask.questionId) return;

    setPanelSize(window.innerWidth * 0.3);
    setTitle('Audit Details');
    setContent(
      <SplitPanelErrorBoundary onReset={closeView}>
        <TaskAuditQuestionResponsePanel
          questionId={selectedTask.questionId}
          questionVersion={selectedTask.questionVersion}
          subQuestionId={selectedTask.subQuestionId}
          auditId={selectedTask.auditId}
          businessUnit={getUserBusinessUnitId()}
          divisionName={selectedTask.divisionName}
          programId={getUserProgramId()}
          descriptionText="Question context and audit response for this task."
        />
      </SplitPanelErrorBoundary>,
    );
  }, [
    selectedTask,
    setPanelSize,
    setTitle,
    setContent,
    closeView,
    getUserBusinessUnitId,
    getUserProgramId,
  ]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            selectedTask && (
              <SpaceBetween size="xs" direction="horizontal">
                {selectedTask.taskStatus === TaskStatus.COMPLETED && canReopenTask() && (
                  <Button
                    variant="primary"
                    iconName="refresh"
                    onClick={() => setShowReopenTaskModal(true)}
                    data-testid="reopen-task-button"
                    disabled={(selectedTask.reopenCount ?? 0) >= MAX_TASK_REOPENS}
                    disabledReason={`Maximum reopen limit (${MAX_TASK_REOPENS}) reached. This task cannot be reopened again.`}
                  >
                    Reopen Task
                  </Button>
                )}
                {selectedTask.taskStatus !== TaskStatus.CANCELLED &&
                  selectedTask.taskStatus !== TaskStatus.COMPLETED && (
                    <>
                      {canChangeAssignee() && (
                        <Button
                          variant="normal"
                          iconName={selectedTask.assigneeLogin ? 'edit' : 'user-profile'}
                          onClick={handleChangeAssignee}
                          data-testid="assignee-button"
                        >
                          {selectedTask.assigneeLogin
                            ? selectedTask.assigneeLogin
                            : 'Change Assignee'}
                        </Button>
                      )}
                      {canCancelTask() && (
                        <Button onClick={handleCancelTask}>Cancel Task</Button>
                      )}
                    </>
                  )}
              </SpaceBetween>
            )
          }
        >
          {getTaskApiStatus.isLoading ? 'Loading Task...' : 'Perform Task'}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Business Unit Mismatch Handler — shows the switch-BU modal and gates the
            task load when a shared/deep-link URL targets a different BU than the profile */}
        <BusinessUnitMismatchHandler
          isSharedMode={!!urlBusinessUnitId}
          isBUMismatch={showBUMismatch}
          currentBusinessUnitId={currentBusinessUnitId}
          requiredBusinessUnitId={urlBusinessUnitId ?? undefined}
          returnPath="/tasks"
          data-testid="task-details-bu-mismatch"
        />

        <ApiStatusFlashBar
          apiStatus={[
            getTaskApiStatus,
            updateAssigneeApiStatus,
            auditorUpdateApiStatus,
            cancelTaskApiStatus,
            reopenTaskApiStatus,
          ]}
          clearMessages={clearAPIStatusMessage}
        />

        {!canPerformTask() && (
          <Alert type="warning" header="Access Restricted">
            You do not have permission to perform tasks. You can only view this task.
          </Alert>
        )}

        {getTaskApiStatus.isLoading && <div>Loading task details...</div>}

        {!showBUMismatch && selectedTask && (
          <>
            {isReadOnlyMode() &&
              selectedTask.taskStatus !== TaskStatus.CANCELLED &&
              selectedTask.taskStatus !== TaskStatus.COMPLETED && (
                <Alert type="info" header="Task Access Information">
                  This task is assigned to <strong>{selectedTask.assigneeLogin}</strong>.
                  Only the assigned user can edit this task.
                </Alert>
              )}

            <TaskDetails
              task={{ ...selectedTask, taskStatus }}
              onDueDateChange={(dueDate: string) => {
                if (selectedTask) {
                  // Ensure due date is in proper ISO format
                  const formattedDueDate =
                    dueDate && !dueDate.includes('T')
                      ? new Date(dueDate + 'T00:00:00.000Z').toISOString()
                      : dueDate;

                  // Update currentFormData with the new due date
                  setCurrentFormData((prev) =>
                    prev
                      ? {
                          ...prev,
                          dueDate: dueDate, // Store in simple format for form data
                        }
                      : null,
                  );

                  auditorUpdate({
                    businessUnitId: getUserBusinessUnitId(),
                    taskId: selectedTask.taskId,
                    dueDate: formattedDueDate,
                    lastUpdatedBy: getUserId(),
                  });
                }
              }}
              isReadOnlyMode={isReadOnlyMode()}
              canChangeDueDate={
                canChangeAssignee() ||
                canCancelTask() ||
                selectedTask.assigneeLogin?.toLowerCase() === getUserId()?.toLowerCase()
              }
              isLoading={getTaskApiStatus.isLoading}
              onViewAuditQuestionResponse={
                selectedTask.questionId ? handleViewAuditQuestionResponse : undefined
              }
            />

            <TaskPerformanceForm
              key={selectedTask.taskId} // Prevent re-mounting when task updates
              task={selectedTask}
              onSave={handleSave}
              onStatusChange={handleStatusChange}
              auditorObservation={taskContent?.auditorObservation}
              isLoading={auditorUpdateApiStatus.isLoading}
              taskStatus={taskStatus}
            />
            <ChangeLog activityHistory={selectedTask.activityHistory} />
          </>
        )}
      </SpaceBetween>

      <ChangeAssigneeModal
        visible={showChangeAssigneeModal}
        onDismiss={() => setShowChangeAssigneeModal(false)}
        onConfirm={handleAssigneeChange}
        currentAssignee={selectedTask?.assigneeLogin || ''}
      />

      <CancelTaskModal
        visible={showCancelTaskModal}
        onDismiss={() => setShowCancelTaskModal(false)}
        onConfirm={handleTaskCancellation}
      />

      <ReopenTaskModal
        visible={showReopenTaskModal}
        onDismiss={() => setShowReopenTaskModal(false)}
        originalDueDate={selectedTask?.dueDate}
        maxReopens={MAX_TASK_REOPENS}
        onSubmit={(data) => {
          reopenTask({
            businessUnitId: getUserBusinessUnitId(),
            taskId,
            newDueDate: data.newDueDate,
            reopenReason: data.reopenReason,
            reopenedBy: getUserId(),
          });
          setShowReopenTaskModal(false);
        }}
      />
    </ContentLayout>
  );
};
